'use client';

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { FORM_RADIUS } from "@/lib/form-tokens"

// `bg-primary`, `bg-secondary` and `bg-destructive` are `!important` rules that generate-colors writes into globals.css, and
// a state variant is an ordinary rule: without the trailing `!` the hover colour below never wins and the button does not
// change colour under the pointer. The `!` goes away with the `!important` in the generator (docs/design-system.md, "Known debt").
const buttonVariants = cva(
  `inline-flex items-center justify-center whitespace-nowrap ${FORM_RADIUS} font-medium transform transition-all duration-200 active:scale-95 active:translate-y-0 outline outline-0 outline-offset-0 outline-transparent focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:!m-0`,
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.03] hover:bg-primary/90!",
        destructive: "bg-destructive text-destructive-foreground hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.03] hover:bg-destructive/90!",
        outline: "border border-input bg-background hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.03] hover:bg-accent hover:text-accent-foreground",
        primary: "bg-primary text-primary-foreground hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.03] hover:bg-primary/90!",
        secondary: "bg-secondary text-secondary-foreground hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.03] hover:bg-secondary/80!",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-emerald-600 text-white hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.03] hover:bg-emerald-700",
        danger: "bg-red-50 text-red-600 hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.03] hover:bg-red-100",
        'soft-danger': "bg-red-50 text-red-500 hover:bg-red-500 hover:text-white hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.03]",
        'soft-warning': "bg-amber-50 text-amber-500 hover:bg-amber-500 hover:text-white hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.03]",
        'soft-success': "bg-emerald-50 text-emerald-500 hover:bg-emerald-500 hover:text-white hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.03]",
        'soft-primary': "bg-indigo-50 text-indigo-500 hover:bg-indigo-500 hover:text-white hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.03]",
        'soft-secondary': "bg-gray-100 text-gray-500 hover:bg-gray-500 hover:text-white hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.03]",
      },
      size: {
        default: "h-8 px-3 py-1.5 text-sm gap-2 [&_svg]:!size-4",
        sm: "h-7 px-2.5 text-xs gap-1.5 [&_svg]:!size-3.5",
        xs: "h-6 px-2 text-xs gap-1.5 [&_svg]:!size-3",
        lg: "h-10 px-8 text-sm gap-2 [&_svg]:!size-5",
        icon: "h-9 w-9 [&_svg]:!size-4",
        'icon-sm': "h-7 w-7 [&_svg]:!size-3.5",
        'icon-xs': "h-6 w-6 [&_svg]:!size-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
